import { useLayoutEffect, useRef, useState } from 'react'

type FitState = { scale: number; portrait: boolean }

function measure(width: number, height: number, stageW: number, stageH: number): FitState {
  return {
    scale: Math.min(width / stageW, height / stageH),
    portrait: height > width,
  }
}

/**
 * Uniformly scales a fixed-size design stage to fit its container, so a 16:9
 * slide keeps identical proportions on phones, tablets and desktops.
 */
export function useFitScale(stageW: number, stageH: number) {
  const ref = useRef<HTMLElement>(null)
  const [fit, setFit] = useState<FitState>(() =>
    typeof window === 'undefined'
      ? { scale: 1, portrait: false }
      : measure(window.innerWidth, window.innerHeight, stageW, stageH),
  )

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const update = () => {
      const { clientWidth, clientHeight } = el
      if (clientWidth && clientHeight) {
        setFit(measure(clientWidth, clientHeight, stageW, stageH))
      }
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [stageW, stageH])

  return { ref, ...fit }
}
